import { useEffect, useState } from "react";

import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";

import Tooltip from "./Tooltip";

type HealthStatus = "loading" | "healthy" | "slow" | "unhealthy";

// Response times (ms) above this are considered slow (orange tick).
const SLOW_THRESHOLD_MS = 1500;
// Give up on the request after this long and treat the domain as unhealthy.
const TIMEOUT_MS = 10000;

function normaliseHealthUrl(domain: string) {
  const trimmed = domain.trim().replace(/\/+$/, "");
  const withProtocol = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  return `${withProtocol}/health`;
}

interface DomainHealthIndicatorProps {
  domain?: string;
}

export default function DomainHealthIndicator({
  domain,
}: DomainHealthIndicatorProps) {
  const [status, setStatus] = useState<HealthStatus>("loading");
  const [responseTime, setResponseTime] = useState<number | null>(null);

  useEffect(() => {
    if (!domain) {
      setStatus("unhealthy");
      setResponseTime(null);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);

    const check = async () => {
      const start = performance.now();
      try {
        const res = await fetch(normaliseHealthUrl(domain), {
          method: "GET",
          signal: controller.signal,
        });
        if (cancelled) return;

        const elapsed = performance.now() - start;
        setResponseTime(elapsed);

        if (!res.ok) {
          setStatus("unhealthy");
        } else if (elapsed > SLOW_THRESHOLD_MS) {
          setStatus("slow");
        } else {
          setStatus("healthy");
        }
      } catch {
        if (!cancelled) {
          setResponseTime(null);
          setStatus("unhealthy");
        }
      } finally {
        clearTimeout(timeout);
      }
    };

    check();

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timeout);
    };
  }, [domain]);

  const responseTimeLabel =
    responseTime !== null ? `${Math.round(responseTime)}ms` : null;

  let icon: JSX.Element;
  let tooltip: string;

  if (status === "loading") {
    icon = (
      <span className="inline-block w-5 h-5 rounded-full border-2 border-gray-300 border-t-transparent animate-spin" />
    );
    tooltip = "Checking domain health…";
  } else if (status === "unhealthy") {
    icon = <XCircleIcon className="w-5 h-5 text-red-500" />;
    tooltip = responseTimeLabel
      ? `Unhealthy — /health did not return a successful response (${responseTimeLabel})`
      : "Unhealthy — /health could not be reached";
  } else {
    icon = (
      <CheckCircleIcon
        className={clsx(
          "w-5 h-5",
          status === "slow" ? "text-orange-500" : "text-green-500",
        )}
      />
    );
    const prefix = status === "slow" ? "Healthy but slow" : "Healthy";
    tooltip = responseTimeLabel ? `${prefix} — ${responseTimeLabel}` : prefix;
  }

  return <Tooltip content={tooltip}>{icon}</Tooltip>;
}
